import React from 'react';
import { useRecruitmentListContext } from 'src/client/contexts/recruitments/RecruitmentList';
import { SearchConditionModal } from 'src/client/components/recruitments/Modal/SearchConditionModal/index';
import { AreaModal } from 'src/client/components/recruitments/Modal/AreaModal/index';
import { LicenseModal } from 'src/client/components/recruitments/Modal/LicenseModal/index';
import { JobBenefitModal } from 'src/client/components/recruitments/Modal/JobBenefitModal/index';
import { WorkingHourOptionModal } from 'src/client/components/recruitments/Modal/WorkingHourOptionModal/index';
import { JobBenefitList } from 'src/client/components/recruitments/Modal/JobBenefitModal/JobBenefitList';
import { WorkingHourOptionList } from 'src/client/components/recruitments/Modal/WorkingHourOptionModal/WorkingHourOptionList';
import { LicenseList } from 'src/client/components/recruitments/Modal/LicenseModal/LicenseList';
import { ConditionsList } from 'src/client/components/recruitments/Modal/SearchConditionModal/ConditionsList';
import {
  useLicenseListQuery,
  useAreaTreeQuery,
  useWorkingHourOptionListQuery,
  useJobBenefitListQuery,
  useJobTaskListQuery,
  useSortOptionListQuery,
  useFacilityFeatureListQuery,
} from 'src/client/generated';
import { JobTaskModal } from './JobTaskModal';
import { JobTaskList } from './JobTaskModal/JobTaskList';
import { SortSettingModal } from 'client/components/recruitments/Modal/SortSettingModal';
import { FacilityFeatureList } from 'client/components/recruitments/Modal/FacilityFeatureListModal/FacilityFeatureList';
import { FacilityFeatureListModal } from 'client/components/recruitments/Modal/FacilityFeatureListModal';

export const SearchModal = () => {
  const { data: jobBenefitsData } = useJobBenefitListQuery({ fetchPolicy: 'cache-first' });
  const { data: workingHourOptionsData } = useWorkingHourOptionListQuery({ fetchPolicy: 'cache-first' });
  const { data: licenseListData } = useLicenseListQuery({ fetchPolicy: 'cache-first' });
  const { data: areaListData } = useAreaTreeQuery({ fetchPolicy: 'cache-first' });
  const { data: jobTasksData } = useJobTaskListQuery({ fetchPolicy: 'cache-first' });
  const { data: sortOptionsData } = useSortOptionListQuery({ fetchPolicy: 'cache-first' });
  const { data: facilityFeatureListData } = useFacilityFeatureListQuery({ fetchPolicy: 'cache-first' });

  const {
    searchQueryParams: { areaIds },
    ui: { displayMode, modeHistory },
    dispatch,
  } = useRecruitmentListContext();

  const lastMode = modeHistory[modeHistory.length - 2];

  const handleClickClose = () => {
    dispatch({
      type: 'changeDisplayMode',
      payload: { displayMode: lastMode },
    });
  };

  const jobBenefitClickHandlers = {
    handleClickClose,
    handleClickClear: () => {
      dispatch({
        type: 'changeJobBenefitIds',
        payload: { jobBenefitIds: [] },
      });
    },
  };

  const workingHourOptionClickHandlers = {
    handleClickClose,
    handleClickClear: () => {
      dispatch({
        type: 'clearWorkingHourOptionIds',
      });
    },
  };

  const licenseClickHandlers = {
    handleClickClose,
    handleClickClear: () => {
      dispatch({
        type: 'changeLicenseIds',
        payload: { licenseIds: [] },
      });
    },
  };

  const facilityFeatureClickHandlers = {
    handleClickClose,
    handleClickClear: () => {
      dispatch({
        type: 'selectFacilityFeatureIds',
        payload: { facilityFeatureIds: [] },
      });
    },
  };

  const searchConditionClickHandlers = {
    handleClickClear: () => {
      dispatch({
        type: 'resetSearchQueryParams',
      });
    },

    handleClickClose: () => {
      dispatch({
        type: 'changeDisplayMode',
        payload: { displayMode: 'list' },
      });
    },
  };

  const areaClickHandlers = {
    handleClickClose: handleClickClose,
    handleClickClear: () => {
      dispatch({
        type: 'changeAreaIds',
        payload: { areaIds: [] },
      });
    },
  };

  const jobTaskClickHandlers = {
    handleClickClose,
    handleClickClear: () => {
      dispatch({
        type: 'changeJobTaskIds',
        payload: { jobTaskIds: [] },
      });
    },
  };

  return (
    <>
      {displayMode === 'area-modal' && (
        <AreaModal areaTreeData={areaListData} areaIds={areaIds ?? undefined} handlers={areaClickHandlers} />
      )}
      {displayMode === 'search-modal' && (
        <SearchConditionModal
          areaIds={areaIds ?? undefined}
          handlers={searchConditionClickHandlers}
          conditionList={
            <ConditionsList
              regions={areaListData?.areaTree.regions ?? []}
              workingHourOptions={workingHourOptionsData?.workingHourOptionList.workingHourOptions ?? []}
              licenses={licenseListData?.licenseList.licenses ?? []}
              facilityFeatures={facilityFeatureListData?.facilityFeatureList.features ?? []}
              jobBenefits={jobBenefitsData?.jobBenefitList.jobBenefits ?? []}
              jobTasks={jobTasksData?.jobTaskList.jobTasks ?? []}
            />
          }
        />
      )}

      {displayMode === 'license-modal' && licenseListData && (
        <LicenseModal
          handlers={licenseClickHandlers}
          licenseList={<LicenseList licenses={licenseListData.licenseList.licenses} />}
        />
      )}

      {displayMode === 'facility-feature-modal' && facilityFeatureListData && (
        <FacilityFeatureListModal
          handlers={facilityFeatureClickHandlers}
          facilityFeatureList={
            <FacilityFeatureList facilityFeatures={facilityFeatureListData?.facilityFeatureList.features} />
          }
        />
      )}
      {displayMode === 'working-hour-option-modal' && workingHourOptionsData && (
        <WorkingHourOptionModal
          handlers={workingHourOptionClickHandlers}
          workingHourOptionList={
            <WorkingHourOptionList
              workingHourOptions={workingHourOptionsData?.workingHourOptionList.workingHourOptions}
            />
          }
        />
      )}
      {displayMode === 'job-benefit-modal' && jobBenefitsData && (
        <JobBenefitModal
          handlers={jobBenefitClickHandlers}
          jobBenefitList={<JobBenefitList jobBenefits={jobBenefitsData.jobBenefitList.jobBenefits} />}
        />
      )}
      {displayMode === 'job-task-modal' && jobTasksData && (
        <JobTaskModal
          handlers={jobTaskClickHandlers}
          jobTaskList={<JobTaskList jobTasks={jobTasksData.jobTaskList.jobTasks || []} />}
        />
      )}
      {displayMode === 'sort-setting-modal' && sortOptionsData && (
        <SortSettingModal sortOptions={sortOptionsData.sortOptionList.options} />
      )}
    </>
  );
};
